// "eCW appointments" — Setup > Data card (2026-10-21).
//
// Cobalt's appointment feed can stall (CDE Medical Associates: nothing newer than
// 10/01), leaving the Eligibility page's upcoming table empty. eCW's own Appointment /
// Schedule report for the next 30 days, exported as Excel and uploaded here, fills the
// appointments table beside Cobalt's rows (services/ecw_appointments_service.py:
// patients matched by eCW account number, Cobalt-owned rows never touched). Nothing
// here names a patient: the response is counts and warnings.
//
//   GET  /api/practices/<id>/ecw-appointments   applies, last_run, formats
//   POST /api/practices/<id>/ecw-appointments   multipart: file, dry_run (synchronous)

import React, { useCallback, useEffect, useState } from "react";

import { formatWhen } from "./DataPage";
import "./EcwAppointmentsCard.css";

const API_URL = process.env.REACT_APP_API_URL || "";

export const HINT =
  "In eCW run the Appointment / Schedule report for the next 30 days and export it as Excel " +
  "(never Save Page); upload it here. Rows land beside Cobalt's; a visit Cobalt already carries is left to Cobalt.";

export function ecwAppointmentsUrl(practiceId) {
  return `${API_URL}/api/practices/${practiceId}/ecw-appointments`;
}

// "40 appointments: 12 created, 28 updated, 3 left to Cobalt; 2 without a patient match"
export function countsLine(counts) {
  if (!counts || typeof counts !== "object") return "";
  const n = (key) => Number(counts[key]) || 0;
  let line =
    `${n("appointments")} appointment${n("appointments") === 1 ? "" : "s"}: ` +
    `${n("created")} created, ${n("updated")} updated, ${n("cobalt_owned")} left to Cobalt`;
  if (n("unmatched") > 0) line += `; ${n("unmatched")} without a patient match`;
  if (n("superseded") > 0) line += `; ${n("superseded")} replaced by Cobalt`;
  if (n("skipped") > 0) line += `; ${n("skipped")} skipped`;
  return line;
}

export function lastRunLine(run) {
  if (!run) return "";
  const parts = [formatWhen(run.uploaded_at)];
  if (run.status) parts.push(run.status === "failed" && run.error ? `failed (${run.error})` : String(run.status));
  if (run.uploaded_by) parts.push(`by ${run.uploaded_by}`);
  return parts.join(" · ");
}

async function request(target, options, fallback) {
  const response = await fetch(target, { credentials: "include", ...options });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const err = new Error((body && body.error) || fallback);
    err.status = response.status;
    throw err;
  }
  if (!body || typeof body !== "object") throw new Error("The response could not be read. Try again.");
  return body;
}

export default function EcwAppointmentsCard({ practiceId }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    if (!practiceId) return Promise.resolve(null);
    return request(ecwAppointmentsUrl(practiceId), {}, "Could not load the eCW appointments status.");
  }, [practiceId]);

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    setData(null);
    setLoadError(null);
    setHidden(false);
    setFile(null);
    setResult(null);
    load()
      .then((body) => {
        if (cancelled || !body) return;
        if (body.applies !== true) {
          setHidden(true);
          return;
        }
        setData(body);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 403) setHidden(true);
        else setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, load]);

  const upload = async () => {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    setResult(null);
    const body = new FormData();
    body.append("file", file);
    body.append("dry_run", dryRun ? "1" : "0");
    try {
      const payload = await request(ecwAppointmentsUrl(practiceId), { method: "POST", body }, "The appointments could not be imported.");
      const line = countsLine(payload.counts);
      setFile(null);
      setResult(payload);
      setMessage(payload.dry_run ? `Preview: ${line}. Nothing was changed.` : `Imported: ${line}.`);
      if (!payload.dry_run) {
        load()
          .then((fresh) => {
            if (fresh && fresh.applies === true) setData(fresh);
          })
          .catch(() => {});
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!practiceId || hidden) return null;
  if (!data && !loadError) return null;

  const lastRun = data && data.last_run;
  const warnings = (result && Array.isArray(result.warnings) && result.warnings) || (lastRun && Array.isArray(lastRun.warnings) && lastRun.warnings) || [];

  return (
    <div className="data-card ecw-appointments" data-testid="ecw-appointments">
      <h2>eCW appointments</h2>
      <p className="data-note">{HINT}</p>
      {loadError && <p className="data-error-line">{loadError}</p>}
      {data && (
        <>
          <p className="ecw-appts-last" data-testid="ecw-appointments-last">
            {lastRun ? (
              <>
                Last upload <span className={lastRun.status === "failed" ? "ecw-appts-failed" : ""}>{lastRunLine(lastRun)}</span>
                {lastRun.status !== "failed" && lastRun.counts && countsLine(lastRun.counts) && <> &middot; {countsLine(lastRun.counts)}</>}
              </>
            ) : (
              "No upload yet."
            )}
          </p>
          {warnings.length > 0 && (
            <ul className="ecw-appts-warnings" data-testid="ecw-appointments-warnings">
              {warnings.map((w, i) => (
                <li key={i}>{String(w)}</li>
              ))}
            </ul>
          )}
          <div className="ecw-appts-upload">
            <label className="ecw-appts-file">
              <span>{(data.formats && data.formats[0]) || "The export (.xlsx, .csv or .xls)"}</span>
              <input
                type="file"
                accept=".xlsx,.csv,.xls,.txt"
                data-testid="ecw-appointments-file"
                disabled={busy}
                onChange={(e) => {
                  const chosen = e.target.files && e.target.files[0];
                  if (chosen) setFile(chosen);
                  e.target.value = "";
                }}
              />
              {file && <span className="data-filename">{file.name}</span>}
            </label>
            <label className="ecw-appts-switch">
              <input type="checkbox" data-testid="ecw-appointments-dry-run" checked={dryRun} disabled={busy} onChange={(e) => setDryRun(e.target.checked)} />
              Dry run (count, change nothing)
            </label>
            <button type="button" className="data-button" data-testid="ecw-appointments-upload" disabled={busy || !file} onClick={upload}>
              {busy ? "Uploading…" : dryRun ? "Dry run" : "Upload"}
            </button>
          </div>
          {error && (
            <div className="data-message data-message-error" data-testid="ecw-appointments-error">
              <span>{error}</span>
              <button type="button" className="data-message-close" aria-label="Dismiss" onClick={() => setError(null)}>
                ×
              </button>
            </div>
          )}
          {message && (
            <div className="data-message data-message-success" data-testid="ecw-appointments-message">
              <span>{message}</span>
              <button type="button" className="data-message-close" aria-label="Dismiss" onClick={() => setMessage(null)}>
                ×
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
